import React, { useState } from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { useNavigate } from "react-router-dom";
import { Brain, Mail, ArrowRight, CheckCircle, RefreshCw, Clock } from "lucide-react";
import { BACKEND_URL } from "./lib/backend";
import { consumePulseNext } from "./pulse/PulseGate";

const API = `${BACKEND_URL}/api`;

export default function AuthPage({ darkMode }) {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const sessionExpired = location.state?.reason === "session_expired";
  const [email, setEmail] = useState("");
  const [consented, setConsented] = useState(false);
  const [step, setStep] = useState("enter"); // enter | sent | dev
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [devToken, setDevToken] = useState("");

  const dm = darkMode;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consented) {
      setError("You must accept the Privacy Policy to continue.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/register`, {
        email: email.trim().toLowerCase(),
        privacy_consented: true,
        consent_source: "auth_page",
      });
      if (res.data.dev_token) {
        setDevToken(res.data.dev_token);
        setStep("dev");
      } else {
        setStep("sent");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDevLogin = async () => {
    if (!devToken) return;
    try {
      // Use POST — scanners cannot pre-consume POST requests
      const res = await axios.post(`${API}/auth/verify`, { token: devToken });
      login(res.data.access_token, res.data.user, res.data.csrf_token);
      navigate(consumePulseNext() || "/feed");
    } catch (err) {
      setError("Token verification failed");
    }
  };

  return (
    <div className={`min-h-screen flex flex-col items-center justify-center px-4 ${dm ? "bg-[#0c1011]" : "bg-[#f8f9fa]"}`}>
      {/* Logo */}
      <div className="flex items-center justify-center mb-12">
        <img
          src={dm ? "/TI_logo_white_crop.png" : "/TI_logo_color_crop.png"}
          alt="tourismintel.ai"
          style={{ height: 36, width: 160, objectFit: "contain", maxWidth: "none" }}
        />
      </div>

      {/* Card */}
      <div
        className={`w-full max-w-md rounded-2xl p-8 ${dm ? "bg-[#1a2122]" : "bg-white"}`}
        style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.15), 0 8px 40px rgba(25,28,29,0.08)" }}
      >
        {step === "enter" && (
          <>
            {sessionExpired && (
              <div
                data-testid="session-expired-banner"
                className={`mb-5 flex items-start gap-3 rounded-lg px-3 py-2.5 text-sm border ${
                  dm
                    ? "border-amber-400/30 bg-amber-400/10 text-amber-200"
                    : "border-amber-200 bg-amber-50 text-amber-900"
                }`}
              >
                <Clock className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  <strong>Session expired.</strong>
                  <span className={`${dm ? "text-amber-200/80" : "text-amber-800"}`}> Please log in again to continue.</span>
                </div>
              </div>
            )}
            <h1 className={`editorial-heading text-2xl font-medium mb-1 ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>
              Access TourismIntel
            </h1>
            <p className={`category-tag mb-8 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
              ENTER YOUR EMAIL — WE&apos;LL SEND A MAGIC LINK. NO PASSWORD REQUIRED.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email */}
              <div>
                <label className={`category-tag block mb-1.5 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
                  <span className="flex items-center gap-1.5"><Mail className="w-3 h-3" />Email address</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); if (error) setError(""); }}
                  placeholder="you@example.com"
                  required
                  data-testid="auth-email-input"
                  className={`w-full px-4 py-3 rounded-xl text-sm outline-none transition-all ${dm
                    ? "bg-[#252e2f] text-slate-200 placeholder:text-slate-600 focus:ring-1 focus:ring-cyan-400/40"
                    : "bg-[#f3f4f5] text-[#191c1d] placeholder:text-[#bac9cc] focus:ring-1 focus:ring-[#006874]/30"}`}
                />
              </div>

              {/* Privacy consent checkbox */}
              <label
                className="flex items-start gap-3 cursor-pointer group"
                data-testid="auth-consent-label"
              >
                <input
                  type="checkbox"
                  checked={consented}
                  onChange={e => { setConsented(e.target.checked); if (error) setError(""); }}
                  data-testid="auth-consent-checkbox"
                  className={`mt-0.5 w-4 h-4 rounded flex-shrink-0 accent-[#006874] ${error && !consented ? "outline outline-1 outline-red-400" : ""}`}
                />
                <span className={`text-xs leading-relaxed ${dm ? "text-slate-500 group-hover:text-slate-400" : "text-[#6b7a7d] group-hover:text-[#3b494c]"}`}>
                  I agree to the{" "}
                  <Link
                    to="/privacy"
                    target="_blank"
                    className={`underline underline-offset-2 ${dm ? "text-cyan-400/80 hover:text-cyan-400" : "text-[#006874]/80 hover:text-[#006874]"}`}
                  >
                    Privacy Policy
                  </Link>
                  {" "}and consent to receive the TourismIntel newsletter.{" "}
                  <span className={`font-medium ${dm ? "text-slate-400" : "text-[#3b494c]"}`}>
                    Subscribing grants access to the platform.
                  </span>
                </span>
              </label>

              {/* Inline error — stays visible alongside checkbox */}
              {error && (
                <p className="text-sm text-red-500 bg-red-50 px-3 py-2 rounded-lg" data-testid="auth-error-msg">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading || !email}
                data-testid="auth-submit-btn"
                className={`w-full py-3 rounded-full category-tag transition-all disabled:opacity-50 flex items-center justify-center gap-2 ${dm
                  ? "bg-cyan-400 text-[#001f24] hover:bg-cyan-300"
                  : "bg-[#006874] text-white hover:bg-[#004f58]"}`}
              >
                {loading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
                {loading ? "Sending…" : "Send Magic Link"}
              </button>
            </form>
          </>
        )}

        {step === "sent" && (
          <div className="text-center py-4">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5 ${dm ? "bg-emerald-400/10" : "bg-emerald-50"}`}>
              <Mail className={`w-7 h-7 ${dm ? "text-emerald-400" : "text-emerald-600"}`} />
            </div>
            <h2 className={`editorial-heading text-2xl font-medium mb-2 ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>
              Check your inbox
            </h2>
            <p className={`text-sm mb-6 leading-relaxed ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
              We sent a magic link to <strong>{email}</strong>.<br />
              Click it to access the site. It expires in 30 minutes.
            </p>
            <p className={`category-tag ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>
              Wrong email?{" "}
              <button
                onClick={() => { setStep("enter"); setError(""); }}
                className={dm ? "text-cyan-400 hover:underline" : "text-[#006874] hover:underline"}
              >
                Go back
              </button>
            </p>
          </div>
        )}

        {step === "dev" && (
          <div className="py-2">
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg mb-5 ${dm ? "bg-amber-400/10" : "bg-amber-50"}`}>
              <span className={`category-tag ${dm ? "text-amber-400" : "text-amber-700"}`}>Dev mode — email not configured</span>
            </div>
            <h2 className={`editorial-heading text-xl font-medium mb-2 ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>
              Click to verify
            </h2>
            <p className={`text-sm mb-5 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
              Resend is not yet configured. Use this button to log in directly.
            </p>
            <button
              onClick={handleDevLogin}
              data-testid="dev-login-btn"
              className={`w-full py-3 rounded-full category-tag transition-all flex items-center justify-center gap-2 ${dm
                ? "bg-cyan-400 text-[#001f24] hover:bg-cyan-300"
                : "bg-[#006874] text-white hover:bg-[#004f58]"}`}
            >
              <CheckCircle className="w-3.5 h-3.5" />
              Log in as {email}
            </button>
            <p className={`category-tag text-center mt-4 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>
              Configure Resend in Admin → Email to enable real magic links
            </p>
          </div>
        )}
      </div>

      <p className={`category-tag mt-8 text-xs text-center tracking-wide ${dm ? "text-slate-700" : "text-[#d9dadb]"}`}>
        © 2026 TourismIntel &nbsp;·&nbsp;
        <a href="/privacy" target="_blank" rel="noopener noreferrer" className="hover:underline">Privacy Policy</a>
        &nbsp;·&nbsp;
        <a href="mailto:mirko@bereadyfor.ai" className="hover:underline">mirko@bereadyfor.ai</a>
      </p>
    </div>
  );
}
